import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useDismissOnOutside } from './useDismissOnOutside';

/*
 * Popover state + outside-click/Escape dismissal, plus a viewport-anchored position for
 * callers that render the panel through a portal.
 *
 * WHY A PORTAL: an `overflow-x-auto` scroller (e.g. the playlist chips row) clips on the
 * block axis too — per spec, once one overflow axis is not `visible` the other resolves to
 * `auto`. An absolutely-positioned panel hanging below a trigger inside such a scroller is
 * invisible. Rendering into document.body escapes the clip.
 *
 * Because the panel then lives outside the trigger's DOM subtree, outside-click detection
 * must check BOTH refs — hence triggerRef + contentRef.
 *
 * Position updates use capture-phase scroll: a scroller's own scroll events do not bubble
 * to window, so without capture the panel detaches from its anchor.
 *
 * SCOPE: this hook owns portalled, below-the-trigger popovers. The other popovers in the app
 * anchor differently (CreditsDisplay opens upward and is not portalled; SidebarDropdown sits
 * right of the trigger, or viewport-centred on mobile), so they are NOT built on it — forcing
 * them through one positioning API is what produced five hand-rolled copies. What they do all
 * share, dismissal, now lives in useDismissOnOutside and every one of them uses it.
 */
export const useMenu = ({ align = 'left', gap = 6, width } = {}) => {
  const [isOpen, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const contentRef = useRef(null);
  const frameRef = useRef(null);

  const close = useCallback(() => {
    const focusWasInside = contentRef.current?.contains(document.activeElement);
    setOpen(false);
    if (focusWasInside) (triggerRef.current?.matches('button') ? triggerRef.current : (triggerRef.current?.querySelector('button[aria-expanded]') || triggerRef.current?.querySelector('button')))?.focus();
  }, []);
  const toggle = useCallback(() => setOpen((v) => !v), []);

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const w = Math.min(width ?? r.width, window.innerWidth - 16);
    // Clamp into the viewport.
    const left = Math.min(Math.max(8, align === 'right' ? r.right - w : r.left), window.innerWidth - w - 8);
    const height = contentRef.current?.scrollHeight || 0;
    const below = Math.max(0, window.innerHeight - r.bottom - gap - 8);
    const above = Math.max(0, r.top - gap - 8);
    const opensAbove = height > below && above > below;
    const maxHeight = opensAbove ? above : below;
    const top = opensAbove ? Math.max(8, r.top - gap - Math.min(height, maxHeight)) : r.bottom + gap;
    const next = { top, left, width: w, maxHeight };
    // Bail when nothing moved: getBoundingClientRect forces layout on every scroll event,
    // and returning a fresh object would re-render the panel even when already correct.
    setPosition((prev) => (
      prev && prev.top === next.top && prev.left === next.left && prev.width === next.width && prev.maxHeight === next.maxHeight ? prev : next
    ));
  }, [align, gap, width]);

  // Before paint, so the panel is positioned on its first visible frame. Callers guard on
  // `position` being non-null for the single render in between.
  useLayoutEffect(() => {
    if (isOpen) updatePosition();
  });

  useDismissOnOutside(isOpen, close, [triggerRef, contentRef]);

  useEffect(() => {
    if (!isOpen) return undefined;

    // rAF-coalesced: the virtualised channel grid fires scroll far faster than paint.
    const onReflow = () => {
      if (frameRef.current) return;
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        updatePosition();
      });
    };

    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [isOpen, updatePosition]);

  // Memoised: callers put the whole `menu` object in useCallback deps (PlaylistChip's
  // handleEdit/handleSync/handleDelete all do), and a fresh literal each render made every
  // one of those memoisations a no-op.
  return useMemo(
    () => ({ isOpen, toggle, close, triggerRef, contentRef, position }),
    [isOpen, toggle, close, position],
  );
};
